import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { subscriptionCancellationReasons } from 'constants/subscriptionCancellationReasons';

import Select from 'components/ui/Select';
import { ActionButton } from 'components/ui/Button';

import {
  FlexWrapper,
  CancellationReasonSelect,
  StyledNoRevert,
  StyledCoolingOffPeriod,
  StyledMessageCoolingOff
} from '../../SubscriptionDetails.styles';

import { StyledButtonGroup, StyledInputWrapper } from '../../../../BookingDetails/Extras/ExtrasHotel/ManageHotel/ManageHotel.styles';
import { isInvalidCoollingOfPeriodReason, isInvalidPastBookingsReason, isInvalidReasonNoPastBookings } from '../Cancellations.utils';

export const CancellationReasons = ({
  bookingWithin14DaysCoolingOffPeriod,
  handleChange,
  handleClick,
  reasonCode,
  userCanCancelSubscription,
  intl,
  isThalysAdminUserOffice,
  hasPastBookings
}) => {
  const [cannotCancelWithReason, setCannotCancelWithReason] = useState(false);
  const hasReason = Boolean(reasonCode);

  const handleCancellationMessages = (reason) => {
    if (bookingWithin14DaysCoolingOffPeriod) {
      const isInvalidReason = isInvalidCoollingOfPeriodReason(reason);
      return setCannotCancelWithReason(isInvalidReason);
    }

    if (hasPastBookings) {
      const isInvalidReason = isInvalidPastBookingsReason(isThalysAdminUserOffice, reason);
      return setCannotCancelWithReason(isInvalidReason);
    }

    const isInvalidReason = isInvalidReasonNoPastBookings(isThalysAdminUserOffice, reason);
    return setCannotCancelWithReason(isInvalidReason);
  };

  return (
    <StyledCoolingOffPeriod>
      <strong>
        <FormattedMessage id="subscriptions.cancel" />
      </strong>
      <StyledMessageCoolingOff data-testid="subscriptions.coolingOff">
        {bookingWithin14DaysCoolingOffPeriod ? (
          <FormattedMessage id="subscription.withinCoolingOff" />
        ) : (
          <FormattedMessage id="subscription.outsideCoolingOff" />
        )}
      </StyledMessageCoolingOff>
      <FlexWrapper>
        <CancellationReasonSelect>
          <StyledInputWrapper>
            <Select
              value={reasonCode}
              required
              defaultValue=""
              data-cy="cancellation-reason"
              data-testid="cancellation-reason"
              onChange={(event) => {
                handleChange(event.target.value);
                handleCancellationMessages(event.target.value);
              }}
            >
              <option value="" disabled>
                {intl.formatMessage({ id: 'subscription.reasonToCalculateRefund' })}
              </option>
              {subscriptionCancellationReasons.map(({ value: optionValue, label: optionLabel }) => (
                <option key={optionValue} value={optionValue}>
                  {optionLabel}
                </option>
              ))}
            </Select>
          </StyledInputWrapper>
        </CancellationReasonSelect>

        <StyledButtonGroup>
          <ActionButton
            type="button"
            data-cy="cancel-subscription"
            data-testid="cancel-subscription-button"
            onClick={handleClick}
            disabled={!hasReason || cannotCancelWithReason}
          >
            <FormattedMessage id="paymentsTab.calcRefund" />
          </ActionButton>
        </StyledButtonGroup>
      </FlexWrapper>
      {!userCanCancelSubscription && (
        <StyledNoRevert data-testid="user-cannot-cancel-message">
          <FormattedMessage id="subscription.needAdminOrSuperUser" />
        </StyledNoRevert>
      )}
      {cannotCancelWithReason && (
        <div data-testid="modal-invalid-reason">
          <FormattedMessage id="subscription.noValidReasonToCancel" />
        </div>
      )}
    </StyledCoolingOffPeriod>
  );
};

CancellationReasons.propTypes = {
  bookingWithin14DaysCoolingOffPeriod: PropTypes.bool,
  handleChange: PropTypes.func,
  handleClick: PropTypes.func,
  reasonCode: PropTypes.string,
  userCanCancelSubscription: PropTypes.bool,
  intl: PropTypes.shape({ formatMessage: PropTypes.func }),
  isThalysAdminUserOffice: PropTypes.bool,
  hasPastBookings: PropTypes.bool
};
